/**
 * ContentFreshness — the A5 freshness + accountability block that closes every
 * content page: "information current as of <Month Year>", who wrote it, who
 * reviewed it, and a machine-readable `dateModified`.
 *
 * Presentation only. The date comes from the per-path lastmod registry
 * (`src/data/content-lastmod.ts`), so a page never advertises a review date
 * that the sitemap contradicts.
 */
import { getLastmod } from "@/data/content-lastmod";

type FreshnessLocale = "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";

const L: Record<
  FreshnessLocale,
  { tag: string; asOf: string; author: string; reviewer: string; note: string; bcp47: string }
> = {
  th: {
    tag: "th-TH",
    asOf: "ข้อมูล ณ",
    author: "ผู้เรียบเรียง",
    reviewer: "ผู้ตรวจทาน",
    note: "เนื้อหานี้เป็นข้อมูลทั่วไปเพื่อการเตรียมเอกสาร ไม่ใช่คำวินิจฉัยของหน่วยงานผู้รับ โปรดตรวจข้อกำหนดฉบับล่าสุดของหน่วยงานปลายทางก่อนยื่นทุกครั้ง",
    bcp47: "th-TH",
  },
  en: {
    tag: "en",
    asOf: "Information current as of",
    author: "Written by",
    reviewer: "Reviewed by",
    note: "General guidance for document preparation, not a decision by the receiving authority. Always check the destination agency's current requirements before lodging.",
    bcp47: "en",
  },
  zh: {
    tag: "zh-CN",
    asOf: "资料截至",
    author: "编写",
    reviewer: "审校",
    note: "本文为文件准备的一般性说明，并非受理机构的审核结论。提交前请核对目标机构的最新要求。",
    bcp47: "zh-CN",
  },
  ja: {
    tag: "ja-JP",
    asOf: "情報の基準日",
    author: "編集",
    reviewer: "監修",
    note: "本記事は書類準備に関する一般的な情報であり、受理機関の判断を保証するものではありません。提出前に提出先の最新要件をご確認ください。",
    bcp47: "ja-JP",
  },
  ko: {
    tag: "ko-KR",
    asOf: "정보 기준일",
    author: "작성",
    reviewer: "감수",
    note: "이 문서는 서류 준비에 관한 일반 정보이며 접수 기관의 판단을 보장하지 않습니다. 제출 전 기관의 최신 요건을 확인하세요.",
    bcp47: "ko-KR",
  },
  vi: {
    tag: "vi-VN",
    asOf: "Thông tin cập nhật đến",
    author: "Biên soạn",
    reviewer: "Thẩm định",
    note: "Nội dung mang tính hướng dẫn chung về chuẩn bị giấy tờ, không phải quyết định của cơ quan tiếp nhận. Hãy kiểm tra yêu cầu mới nhất trước khi nộp.",
    bcp47: "vi-VN",
  },
  id: {
    tag: "id-ID",
    asOf: "Informasi per",
    author: "Disusun oleh",
    reviewer: "Ditinjau oleh",
    note: "Panduan umum penyiapan dokumen, bukan keputusan instansi penerima. Periksa persyaratan terbaru instansi tujuan sebelum mengajukan.",
    bcp47: "id-ID",
  },
};

const DEFAULT_AUTHOR = "ทีมเนื้อหากฎหมาย Thai Notary Law";
const DEFAULT_AUTHOR_INTL = "Thai Notary Law legal content team";
const DEFAULT_REVIEWER = "ทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)";
const DEFAULT_REVIEWER_INTL = "Notarial Services Attorney, Lawyers Council of Thailand";

export function ContentFreshness({
  path,
  locale = "th",
  author,
  reviewer,
  className,
}: {
  /** Site path used to resolve the real lastmod timestamp, e.g. `/naati`. */
  path: string;
  locale?: FreshnessLocale;
  author?: string;
  reviewer?: string;
  className?: string;
}) {
  const t = L[locale] ?? L.th;
  const iso = getLastmod(path);
  const d = new Date(iso);
  const asOf = new Intl.DateTimeFormat(t.bcp47, { year: "numeric", month: "long" }).format(d);
  const who = author ?? (locale === "th" ? DEFAULT_AUTHOR : DEFAULT_AUTHOR_INTL);
  const by = reviewer ?? (locale === "th" ? DEFAULT_REVIEWER : DEFAULT_REVIEWER_INTL);

  return (
    <aside
      lang={t.tag}
      className={`not-prose mt-10 rounded-xl border border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground ${className ?? ""}`}
      aria-label={t.asOf}
    >
      <p className="m-0">
        <strong className="text-foreground">
          {t.asOf}{" "}
          <time dateTime={iso} itemProp="dateModified">
            {asOf}
          </time>
        </strong>
      </p>
      <p className="m-0 mt-1">
        {t.author}: {who} · {t.reviewer}: {by}
      </p>
      <p className="m-0 mt-2 text-xs">{t.note}</p>
    </aside>
  );
}
